// Progression spine: the daily chore ledger, the eight certificate badges, and
// the Parent Corner readiness metrics (EDUCATION-DESIGN.md §4 and §5.5).
//
// Chores refresh on the real calendar day, not the sim clock — a kid's routine
// runs on their day, not the farm's.

import { bus } from '../core/events';
import { clamp } from '../core/math';
import type { BadgeId, CareLogEntry, ChoreId, GameState } from '../core/types';
import { BREED_FACT_IDS } from '../edu/facts';
import { daylightHours } from './eggs';
import { effectsOf } from './items';
import { calendarDayKey, EVENING_CHORE_HOUR, hourOfDay, MORNING_CHORE_HOUR } from './time';
import { applicableChores } from './welfare';
import { VETERAN_UNLOCK_BADGES } from './veteran';

/** Whether every chore this yard can currently do has been done today. */
function checklistComplete(state: GameState): boolean {
  const done = state.progression.choresDoneToday;
  return applicableChores(state).every((chore) => done.includes(chore));
}

/** ECONOMY.md §1: the core daily "wage". No rollover stacking. R1: 15→25 so coop (80) lands ≤3 days. */
export const CHECKLIST_REWARD = 25;
export const BADGE_REWARD = 100;

/** EDUCATION-DESIGN.md §5.5 — the parent trial window. */
export const TRIAL_DAYS = 21;

const BADGE_ORDER: BadgeId[] = [
  'brooder_boss',
  'clean_coop',
  'egg_collector',
  'storm_ready',
  'predator_proof',
  'breed_scholar',
  'genetics_junior',
  'good_neighbor',
];

export function allBadges(): BadgeId[] {
  return [...BADGE_ORDER];
}

/**
 * Rolls the ledger over when the real calendar day changes. Yesterday's record
 * is closed out first, so a missed day is visible in the Parent Corner rather
 * than silently erased.
 */
/**
 * Opens the event-driven store gates when the event they are named for has
 * actually happened.
 *
 * `isUnlocked` falls through to `progression.unlocks` for every gate it does
 * not special-case, and the only thing that ever pushed into that array was the
 * first escape. So the covered feeder, the coop light, the decoy egg and the
 * footbath were permanently unbuyable — four catalogue items no player action
 * could reach. Each gate is named for its teachable moment, and the item is
 * meant to appear right as the lesson lands (ECONOMY.md §2).
 */
export function evaluateUnlocks(state: GameState): string[] {
  const p = state.progression;
  const opened: string[] = [];
  const open = (gate: string, when: boolean) => {
    if (when && !p.unlocks.includes(gate)) {
      p.unlocks.push(gate);
      opened.push(gate);
    }
  };

  // She has learned to eat her own eggs; the decoy is the standard answer.
  open(
    'egg_eating_habit',
    state.flock.some((c) => c.eggs.eggEatingHabit),
  );
  // Short days are cutting production, which is what the coop light is for.
  open(
    'short_day_dip',
    daylightHours(state.weather) < SHORT_DAY_HOURS &&
      state.flock.some((c) => c.stage === 'adult'),
  );
  // The biosecurity lesson has been read, so the footbath means something now.
  // The card's id is `quarantine`; its trigger is what the gate is named after.
  open('biosecurity_fact_seen', p.factsSeen.includes('quarantine'));
  // Wild birds at an open feeder is how respiratory disease walks into a
  // backyard flock, and a covered feeder is the answer. There is no wild-bird
  // fact card to hang this on, so the gate opens on the pathway itself: an
  // uncovered feeder standing outside and a bird that has caught something.
  open(
    'wild_bird_event',
    state.structures.some((s) => effectsOf(s).providesFeed && !effectsOf(s).coveredFeeder) &&
      state.flock.some((c) => c.conditions.some((cond) => cond.id === 'respiratory')),
  );
  // A hawk has been at the flock. Netting over the run is the answer, and it
  // is the only one — cover a bird has to be standing under leaves an aerial
  // hunter a residual chance no other purchase can reduce.
  // A near miss counts: the lesson is that something came, not that it won.
  open('first_predator_attack', Object.keys(state.predators?.pressure ?? {}).length > 0);
  return opened;
}

/** Below this a keeper would notice the basket thinning out. */
const SHORT_DAY_HOURS = 11.5;

export function rolloverDay(state: GameState, dayKey = calendarDayKey()): boolean {
  const p = state.progression;
  if (p.currentDay === dayKey) return false;

  if (p.currentDay !== '') {
    const complete = checklistComplete(state);
    p.consecutiveCleanDays = complete ? p.consecutiveCleanDays + 1 : 0;

    // A day the neighbour did not complain. Nothing wrote this counter, so
    // `good_neighbor` — thirty quiet days — could never be earned, and with it
    // the certificate that needs all eight badges was unreachable.
    p.daysWithoutComplaint = p.complainedToday ? 0 : p.daysWithoutComplaint + 1;
    p.complainedToday = false;
    // Days already logged by recordChore keep their timings; a fully missed
    // day still needs an entry, otherwise the streak math sees no gap.
    if (!p.careLog.some((e) => e.day === p.currentDay)) {
      p.careLog.push(emptyLogEntry(p.currentDay));
    }
    consumeSupplements(state);
  }

  p.currentDay = dayKey;
  p.choresDoneToday = [];
  // Only the trial window matters, and it keeps saves from growing forever.
  p.careLog = p.careLog.slice(-TRIAL_DAYS * 2);
  return true;
}

/** Bird-days one container of grit or oyster shell covers. */
const SUPPLEMENT_BIRD_DAYS = 30;

/**
 * Eats into the grit and oyster shell.
 *
 * `hasGrit` and `hasCalcium` only ask whether the inventory count is above
 * zero, and nothing ever decremented either — so eight Eggits bought calcium
 * for the rest of the game, and six bought grit. Both are consumables in a real
 * coop and both are supposed to be a standing small cost, which is the point
 * the shell-quality and gizzard lessons hang on.
 *
 * One container covers `SUPPLEMENT_BIRD_DAYS` bird-days, so a bigger flock
 * empties it proportionally faster. Counted off the sim day rather than an
 * accumulator field, so it costs nothing in the save.
 */
function consumeSupplements(state: GameState): void {
  const birds = state.flock.filter((c) => c.alive).length;
  if (birds === 0) return;
  const everyNDays = Math.max(1, Math.round(SUPPLEMENT_BIRD_DAYS / birds));
  if (Math.floor(state.simHour / 24) % everyNDays !== 0) return;

  for (const id of ['grit', 'oyster_shell'] as const) {
    const have = state.inventory[id] ?? 0;
    if (have > 0) state.inventory[id] = have - 1;
  }
}

function emptyLogEntry(day: string): CareLogEntry {
  return {
    day,
    morningChoreMinutesLate: null,
    eveningChoreMinutesLate: null,
    choresCompleted: 0,
    choresComplete: false,
    unprompted: false,
    incidents: 0,
  };
}

function logFor(state: GameState, day: string): CareLogEntry {
  const existing = state.progression.careLog.find((e) => e.day === day);
  if (existing) return existing;
  const entry = emptyLogEntry(day);
  state.progression.careLog.push(entry);
  return entry;
}

export interface ChoreRecordResult {
  /** False when the chore was already ticked off today. */
  recorded: boolean;
  /** True on the tick that completed the whole day's checklist. */
  checklistComplete: boolean;
  reward: number;
}

/**
 * Marks one chore done. `prompted` means the HUD chip was already glowing —
 * the Parent Corner's "unassisted" metric is the inverse of that.
 */
export function recordChore(
  state: GameState,
  chore: ChoreId,
  options: { prompted?: boolean; dayKey?: string } = {},
): ChoreRecordResult {
  const dayKey = options.dayKey ?? calendarDayKey();
  rolloverDay(state, dayKey);
  const p = state.progression;

  if (p.choresDoneToday.includes(chore)) {
    return { recorded: false, checklistComplete: false, reward: 0 };
  }
  p.choresDoneToday.push(chore);
  bus.emit('CHORE_COMPLETED', { chore });

  const entry = logFor(state, dayKey);
  entry.choresCompleted = p.choresDoneToday.length;
  // One prompted chore is enough to mark the day assisted; the metric is about
  // whether the kid needed reminding, not about scoring each individual task.
  if (!options.prompted && entry.choresCompleted === 1) entry.unprompted = true;
  if (options.prompted) entry.unprompted = false;
  recordPromptness(entry, hourOfDay(state.simHour));

  if (!checklistComplete(state)) return { recorded: true, checklistComplete: false, reward: 0 };
  entry.choresComplete = true;

  // R3: wage pays once per day. Extra chores after completion (e.g. collecting
  // a later egg) re-hit this path and re-paid +25 every time.
  if (p.choreCompletionDays.includes(dayKey)) {
    return { recorded: true, checklistComplete: true, reward: 0 };
  }
  p.choreCompletionDays.push(dayKey);
  state.eggits += CHECKLIST_REWARD;
  bus.emit('CHECKLIST_COMPLETE', { day: dayKey, reward: CHECKLIST_REWARD });
  return { recorded: true, checklistComplete: true, reward: CHECKLIST_REWARD };
}

/** Minutes past the morning or evening chore window, whichever this is. */
function recordPromptness(entry: CareLogEntry, hour: number): void {
  const midday = (MORNING_CHORE_HOUR + EVENING_CHORE_HOUR) / 2;
  const late = (target: number) => Math.max(0, Math.round((hour - target) * 60));
  if (hour < midday) {
    if (entry.morningChoreMinutesLate === null) entry.morningChoreMinutesLate = late(MORNING_CHORE_HOUR);
  } else if (entry.eveningChoreMinutesLate === null) {
    entry.eveningChoreMinutesLate = late(EVENING_CHORE_HOUR);
  }
}

/** A medical incident today — feeds the Parent Corner log. */
export function recordIncident(state: GameState, dayKey = calendarDayKey()): void {
  rolloverDay(state, dayKey);
  logFor(state, dayKey).incidents += 1;
}

/**
 * Accumulates brooder time-in-spec for Brooder Boss. `tsi` is the temperature
 * stress index from welfare.ts: degrees away from the age-appropriate target.
 */
export function recordBrooderTime(state: GameState, tsi: number, hours: number): void {
  const p = state.progression;
  p.brooderTotalHours += hours;
  if (tsi <= BROODER_OPTIMAL_TSI) p.brooderOptimalHours += hours;
}

/** Within 5°F of target counts as optimal — the brooder is run by eye. */
export const BROODER_OPTIMAL_TSI = 5;
export const BROODER_OPTIMAL_FRACTION = 0.8;
/** Minimum tracked hours before Brooder Boss can fire — proves raising, not spawning (R2: starter layer must not auto-award +100 on boot). */
export const BROODER_MIN_HOURS = 48;

/** Whether each badge's proof is currently satisfied. */
export function badgeEarned(badge: BadgeId, state: GameState): boolean {
  const p = state.progression;
  switch (badge) {
    case 'brooder_boss': {
      // Needs both: the birds actually grew out, and the brooder was run well.
      const grewOut = state.flock.some((c) => c.stage === 'pullet' || c.stage === 'adult');
      if (!grewOut || p.brooderTotalHours < BROODER_MIN_HOURS) return false;
      return p.brooderOptimalHours / p.brooderTotalHours >= BROODER_OPTIMAL_FRACTION;
    }
    case 'clean_coop':
      return p.consecutiveCleanDays >= 7;
    case 'egg_collector':
      return p.eggsCollected >= 50;
    case 'storm_ready':
      return (state.storm.score ?? 0) >= 100;
    case 'predator_proof':
      return p.nightsWithoutPredator >= 14 && state.flock.length >= 4;
    case 'breed_scholar':
      return p.breedQuizPassed && BREED_FACT_IDS.every((id) => p.factsSeen.includes(id));
    case 'genetics_junior':
      return p.geneticsPredictionCorrect;
    case 'good_neighbor':
      return p.daysWithoutComplaint >= 30;
  }
}

/**
 * Awards any badge whose proof now holds. Returns only the newly earned ones,
 * so the caller can celebrate each exactly once.
 */
export function evaluateBadges(state: GameState): BadgeId[] {
  const earned: BadgeId[] = [];
  for (const badge of BADGE_ORDER) {
    if (state.progression.badges.includes(badge)) continue;
    if (!badgeEarned(badge, state)) continue;
    state.progression.badges.push(badge);
    state.eggits += BADGE_REWARD;
    bus.emit('BADGE_EARNED', { badge });
    earned.push(badge);
  }
  if (
    !state.progression.certificateEarned &&
    BADGE_ORDER.every((b) => state.progression.badges.includes(b))
  ) {
    state.progression.certificateEarned = true;
  }
  // (NEMO-THINKS Tier 1 #3) The veteran panel was previously gated by
  // `shouldUnlockVeteran(state)` (a derived check) but the persistent
  // `state.veteran.unlocked` flag was never set. Now the unlock fires the
  // first time a player crosses the badge threshold, and an event lets the
  // HUD celebrate it instead of the panel silently enabling.
  if (!state.veteran.unlocked && VETERAN_UNLOCK_BADGES <= state.progression.badges.length) {
    state.veteran.unlocked = true;
    bus.emit('VETERAN_UNLOCKED', { badges: state.progression.badges.length });
  }
  return earned;
}

// --- Parent Corner (EDUCATION-DESIGN.md §5.5) ---

export interface ReadinessMetrics {
  /** Days in the trial window that have any record at all. */
  daysTracked: number;
  /** Current run of consecutive fully-complete days. */
  careStreak: number;
  /** Share of chore sessions done inside their window, 0–100. */
  promptnessPercent: number;
  averageMinutesLate: number;
  /** Share of days completed without the HUD prompting first, 0–100. */
  unassistedPercent: number;
  incidents: number;
  /** True once the routine holds well enough to recommend real chicks. */
  ready: boolean;
  daysRemaining: number;
}

/** On time means within half an hour of the chore window opening. */
export const ON_TIME_MINUTES = 30;
const READY_PROMPTNESS = 70;
const READY_UNASSISTED = 60;
const READY_DAYS = 14;

export function readinessMetrics(state: GameState): ReadinessMetrics {
  const window = state.progression.careLog.slice(-TRIAL_DAYS);
  const timings = window.flatMap((e) =>
    [e.morningChoreMinutesLate, e.eveningChoreMinutesLate].filter(
      (v): v is number => v !== null,
    ),
  );
  const onTime = timings.filter((m) => m <= ON_TIME_MINUTES).length;
  const completeDays = window.filter((e) => e.choresComplete);
  const unassisted = completeDays.filter((e) => e.unprompted).length;

  const promptnessPercent = timings.length ? Math.round((onTime / timings.length) * 100) : 0;
  const unassistedPercent = completeDays.length
    ? Math.round((unassisted / completeDays.length) * 100)
    : 0;
  const daysRemaining = clamp(READY_DAYS - completeDays.length, 0, READY_DAYS);

  return {
    daysTracked: window.length,
    careStreak: state.progression.consecutiveCleanDays,
    promptnessPercent,
    averageMinutesLate: timings.length
      ? Math.round(timings.reduce((a, b) => a + b, 0) / timings.length)
      : 0,
    unassistedPercent,
    incidents: window.reduce((sum, e) => sum + e.incidents, 0),
    ready:
      completeDays.length >= READY_DAYS &&
      promptnessPercent >= READY_PROMPTNESS &&
      unassistedPercent >= READY_UNASSISTED,
    daysRemaining,
  };
}
